// Typed wrappers around the SSO handshake + silent-refresh endpoints
// exposed under `/api/v1/auth/...` by the FastAPI backend.
//
// The backend is the only component that ever decodes the WordPress JWT
// (signature, exp and iss are validated server-side) — the SPA's job is
// strictly to forward the token from the URL into POST /auth/login-bridge
// and let the backend mint a Forge Ultra session token in exchange.
// After that the SPA holds the Forge access JWT in localStorage AND
// receives an httpOnly refresh cookie scoped to /api/v1/auth that the
// browser sends automatically on /auth/refresh calls.

import axios from 'axios';

import { apiClient } from './client';
import type { UserProfile } from './users';

// Successful payload from POST /auth/login-bridge.
//
// The body carries the freshly-minted Forge access token so the SPA
// can store it in localStorage without an extra round-trip. ``expires_in``
// is the lifetime in seconds (currently 1 hour); the SPA can use it to
// schedule a *proactive* refresh just before expiry instead of waiting
// for the first 401, but the response interceptor will catch the 401
// fallback if it doesn't.
export interface LoginBridgeResponse {
    user: UserProfile;
    access_token: string;
    token_type: 'bearer';
    expires_in: number;
}

// Successful payload from POST /auth/refresh. Mirrors the access-token
// half of LoginBridgeResponse so the SPA has one consistent shape to
// consume. The new refresh cookie is set as a side effect (Set-Cookie
// header on the response) and never appears in the body — raw refresh
// tokens are httpOnly by design.
export interface RefreshResponse {
    access_token: string;
    token_type: 'bearer';
    expires_in: number;
}

// Exchange a WP-issued JWT for a Forge Ultra session.
//
// Backend flow:
//   1. verifies the WP JWT (signature, exp, iss) with JWT_AUTH_SECRET_KEY,
//   2. authorises (administrator / Ki-Wealth Premium / Ki-Wealth Professional),
//   3. mirrors the user into the local DB,
//   4. mints a SHORT-LIVED Forge access JWT (signed with FORGE_ACCESS_SECRET,
//      iss=https://forge-ultra.ki-wealth.com) and returns it in the body,
//   5. mirrors the same value into the `forge_session` httpOnly cookie,
//   6. issues a long-lived refresh token in the `forge_refresh` httpOnly
//      cookie scoped to /api/v1/auth.
//
// On 401/403 the caller should bounce the user back to the WordPress
// bridge URL (or membership page) — the apiClient interceptor handles
// 401 globally; 403 is forwarded as an AxiosError to the caller.
export async function exchangeTokenForSession(
    token: string,
): Promise<LoginBridgeResponse> {
    const { data } = await apiClient.post<LoginBridgeResponse>(
        '/auth/login-bridge',
        { token },
    );
    return data;
}

// Silent re-auth: trade the `forge_refresh` cookie for a new access JWT.
//
// IMPORTANT: this MUST go through a fresh axios instance, NOT the
// shared `apiClient`. Reusing the apiClient would re-attach the
// (likely expired) Authorization header AND re-enter the 401
// interceptor on failure — turning a clean "refresh failed, bounce
// to WP" into a recursion loop. The dedicated client below has:
//
//   * `withCredentials: true`  — so the browser actually sends the
//      `forge_refresh` cookie despite the cross-site call,
//   * no Authorization interceptor — so we don't waste effort
//      attaching a stale Bearer token the backend would just ignore,
//   * no response interceptor    — failures propagate as plain
//      AxiosError for the caller to react to.
//
// Returns the new access token. Throws an AxiosError on any non-2xx
// response (including the canonical 401 "refresh expired" branch).
const refreshClient = axios.create({
    baseURL: apiClient.defaults.baseURL,
    timeout: 15_000,
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true,
});

export async function refreshAccessToken(): Promise<RefreshResponse> {
    const { data } = await refreshClient.post<RefreshResponse>(
        '/auth/refresh',
    );
    return data;
}

// Best-effort logout: revokes the server-side refresh token AND clears
// both cookies. We don't wait on the result — even if the call fails
// the SPA still clears its local state and bounces the user back to
// ki-wealth.com.
export async function logout(): Promise<void> {
    try {
        // Use the dedicated refresh client so the request:
        //   * carries the `forge_refresh` cookie (apiClient sends
        //     cookies too, but only ones on path '/' — refresh is
        //     scoped to /api/v1/auth, so the cookie travels here either way),
        //   * does NOT trigger the apiClient's 401 interceptor if the
        //     logout call itself 401s (no refresh cookie present).
        await refreshClient.post('/auth/logout');
    } catch {
        // Intentionally swallowed — see fn docstring.
    }
}
